import { defineStore } from "pinia";

import { $streemApiV1 } from "shared/boot/api";
import { getCurrentISODate, getCurrentTimestamp } from "shared/helpers/date";
import type { Stream } from "shared/types";

import useGlobalStore from "../global";
import useStreamsStore from "../streams";
import { useUserStore } from "../user";

export type MentionCountStreams = {
  [key: string]: {
    count: number;
    refreshAt: number;
  };
};

export type UnreadMentionCounts = {
  [key: string]: { streamRefreshedAt: number; unreadCount: number };
};

interface RootState {
  streams: MentionCountStreams;
}

const sum = (left: number, right: number) => left + right;

export const useMentionCountsStore = defineStore("mentionCountsStore", {
  actions: {
    clearMentionsCount(stream: Stream) {
      this.setMentionCount({ count: 0, stream });

      useStreamsStore().putStream({
        data: {
          onlyLastSeenUpdated: stream.onlyLastSeenUpdated !== false,
          refreshed_at: getCurrentISODate(),
        },
        id: Number(stream.id),
      });

      return $streemApiV1.put<Stream>(`streams/${stream.id}`, {
        params: {
          refreshed_at: getCurrentTimestamp(),
        },
      });
    },

    clearStreamsMentionsCount(streams: Stream[]) {
      streams.forEach((stream) => {
        if (this.mentionCount(stream.id!) > 0) {
          this.clearMentionsCount({ ...stream, onlyLastSeenUpdated: false });
        }
      });
    },
    async fetchMentionCounts(params?: {
      first_load?: boolean;
      streamIds?: string[];
    }) {
      if (!useUserStore().isLoggedIn || useGlobalStore().technicalServerError) {
        return;
      }

      try {
        const { data } = await $streemApiV1.get<UnreadMentionCounts>(
          "streams/unread_mention_counts",
          {
            headers: {
              "Key-Inflection": "camel",
            },
            params,
          }
        );

        const putStreams = new Array<Partial<Stream>>();
        const setMentionCountStreams = {};

        const refreshAt = Date.now();

        Object.entries(data).forEach(([id, value]) => {
          const count = Number.isInteger(value)
            ? Number(value)
            : value.unreadCount;

          Object.assign(setMentionCountStreams, {
            [Number(id)!]: { count, refreshAt },
          });

          const refreshedAt = value?.streamRefreshedAt;

          if (refreshedAt) {
            putStreams.push({
              id: Number(id),
              onlyLastSeenUpdated: true,
              refreshed_at: refreshedAt,
            });
          }
        });

        Object.assign(this.streams, setMentionCountStreams);
        useStreamsStore().putStreams(putStreams);
      } catch {
        // Counts are re-fetched on the next poll.
      }
    },
    setMentionCount({
      count,
      refreshAt = Date.now(),
      stream,
    }: {
      count: number;
      refreshAt?: number;
      stream: Partial<Stream>;
    }) {
      Object.assign(this.streams, {
        [stream.id!]: {
          count,
          refreshAt,
        },
      });
    },
  },

  getters: {
    mentionCount: (state: RootState) => (id: number) => {
      const stream = state.streams[id];

      return stream ? stream.count : 0;
    },
    totalMentionsCount(state: RootState) {
      Object.keys(state.streams)
        .map((id) => state.streams[id].count)
        .reduce(sum, 0);
    },
  },
  state: (): RootState => ({
    streams: {},
  }),
});

export default useMentionCountsStore;
